import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

let keycloakInstance = null;

export function AuthProvider({ children }) {
  // authMode sofort aus Cache lesen um Flackern zu vermeiden
  const cachedMode = localStorage.getItem("auth_mode_cache");
  const [state, setState] = useState({
    ready: false,
    authenticated: false,
    user: null,
    authMode: cachedMode,
  });

  useEffect(() => {
    fetch("/api/auth-mode")
      .then(r => r.json())
      .then(({ mode, sso }) => {
        localStorage.setItem("auth_mode_cache", mode);
        if (mode === "internal") {
          initInternalAuth(mode);
        } else {
          // Aufräumen: falls hier mal ein interner Login getestet/genutzt
          // wurde (z.B. während einer Fehlersuche) und der Mandant jetzt
          // auf SSO läuft, darf dieser alte Token NIE mehr verwendet
          // werden - sonst kann es (durch eine kurze Race Condition, bevor
          // der Keycloak-Token geladen ist) passieren, dass genau dieser
          // veraltete, möglicherweise nicht-admin Token für einzelne
          // Anfragen greift, obwohl man eigentlich per SSO als Admin
          // eingeloggt ist.
          localStorage.removeItem("internal_token");
          localStorage.removeItem("internal_user");
          initKeycloak(mode, sso);
        }
      })
      .catch(() => {
        const mode = cachedMode || "keycloak";
        if (mode === "internal") {
          initInternalAuth(mode);
        } else {
          localStorage.removeItem("internal_token");
          localStorage.removeItem("internal_user");
          initKeycloak(mode, null);
        }
      });
  }, []);

  const initInternalAuth = (mode) => {
    const token = localStorage.getItem("internal_token");
    const userStr = localStorage.getItem("internal_user");
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        window._internalToken = token;
        setState({
          ready: true,
          authenticated: true,
          authMode: mode,
          user: {
            sub: String(user.id),
            preferred_username: user.email,
            email: user.email,
            display_name: user.name,
            roles: [user.rolle],
            isAdmin: ["superadmin", "admin"].includes(user.rolle),
          }
        });
        return;
      } catch {}
    }
    setState({ ready: true, authenticated: false, authMode: mode, user: null });
  };

  const initKeycloak = async (mode, ssoConfig) => {
    if (!ssoConfig || !ssoConfig.issuer || !ssoConfig.client_id) {
      // Kein SSO für diesen Mandanten konfiguriert - nichts zu tun, kein
      // automatischer Redirect zu einem nicht existierenden Provider.
      setState({ ready: true, authenticated: false, authMode: mode, user: null });
      return;
    }

    const kc = await import("./keycloak");
    let instance;
    try {
      instance = kc.configure(ssoConfig);
    } catch (e) {
      console.error(e);
      setState({ ready: true, authenticated: false, authMode: mode, user: null });
      return;
    }
    keycloakInstance = instance;

    instance
      .init({ onLoad: "login-required", checkLoginIframe: false, pkceMethod: "S256" })
      .then((authenticated) => {
        if (authenticated) {
          const token = instance.tokenParsed;
          const roles = [
            ...(token?.realm_access?.roles || []),
            ...(token?.resource_access?.[instance.clientId]?.roles || []),
          ];
          window._kcToken = instance.token;
          kc.startTokenRefresh();
          instance.onTokenRefreshed = () => { window._kcToken = instance.token; };
          setState({
            ready: true,
            authenticated: true,
            authMode: mode,
            user: {
              sub: token?.sub,
              preferred_username: token?.preferred_username,
              email: token?.email,
              display_name: `${token?.given_name || ""} ${token?.family_name || ""}`.trim() || token?.preferred_username,
              roles,
              isAdmin: (token?.groups || []).some(g => g === "/sitzungsdienst/sitzungsdienst-admin" || g.endsWith("/sitzungsdienst-admin")),
            }
          });
        } else {
          instance.login();
        }
      })
      .catch(() => {
        setState({ ready: true, authenticated: false, authMode: mode, user: null });
      });
  };

  const logout = () => {
    if (state.authMode === "internal") {
      localStorage.removeItem("internal_token");
      localStorage.removeItem("internal_user");
      window.location.href = "/login";
    } else if (keycloakInstance) {
      keycloakInstance.logout();
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export async function getAuthHeader() {
  // Keycloak-Modus: Token proaktiv VOR jeder Anfrage auffrischen, statt
  // sich auf den 60-Sekunden-Hintergrund-Timer zu verlassen. Ohne das
  // konnte eine Anfrage kurz nach Ablauf des (oft nur wenige Minuten
  // gültigen) Access-Tokens mit 401 fehlschlagen, obwohl ein gültiger
  // Token Sekunden später wieder verfügbar gewesen wäre.
  //
  // Zusätzliche Absicherung: falls die lokale keycloakInstance-Variable
  // aus irgendeinem Grund (noch) nicht gesetzt ist, wird zur Sicherheit
  // direkt bei keycloak.js nachgefragt (dieselbe Instanz, aber über die
  // dortige, garantiert aktuelle Referenz statt eines lokalen Snapshots).
  let kcInst = keycloakInstance;
  if (!kcInst) {
    try {
      const kc = await import("./keycloak");
      kcInst = kc.getKeycloak();
    } catch (e) { /* kein Keycloak-Modul geladen - z.B. interner Auth-Modus */ }
  }

  if (kcInst) {
    try {
      await kcInst.updateToken(30);
      window._kcToken = kcInst.token;
    } catch (e) {
      console.warn("Token-Refresh vor Anfrage fehlgeschlagen:", e);
    }
  } else {
    console.warn("getAuthHeader: keine Keycloak-Instanz verfügbar (weder lokal noch über keycloak.js)");
  }

  if (window._kcToken) return { Authorization: `Bearer ${window._kcToken}` };

  // Sicherheitsnetz gegen die Race Condition beim allerersten Laden (Keycloak-
  // Init ist async, window._kcToken kurzzeitig noch nicht gesetzt): wenn der
  // zuletzt bekannte Auth-Modus NICHT "internal" ist, darf ein evtl. noch
  // vorhandener alter internal_token NIE verwendet werden - sonst könnte in
  // genau diesem kurzen Fenster ein veralteter, falsch berechtigter Token
  // für einzelne Anfragen greifen, obwohl der Mandant eigentlich per SSO läuft.
  if (localStorage.getItem("auth_mode_cache") !== "internal") {
    console.warn("getAuthHeader: kein Token verfügbar, sende Anfrage OHNE Authorization-Header");
    return {};
  }

  const internalToken = localStorage.getItem("internal_token");
  // Sanity-Check: ein gültiger JWT hat 2 Punkte (3 Segmente)
  if (internalToken && (internalToken.match(/\./g) || []).length === 2) {
    return { Authorization: `Bearer ${internalToken}` };
  }
  // Kaputten Token entsorgen damit er nicht wieder genutzt wird
  if (internalToken) {
    try { localStorage.removeItem("internal_token"); } catch(e) {}
  }
  return {};
}
